import React, { useState, useRef, useEffect } from "react";
import propTypes from "prop-types";
import AvatarPreview from "../avatar-preview";
import { AvatarHanger } from "./AvatarHanger";
import { FormattedMessage, useIntl } from "react-intl";
import { resolveAvatarSrcWithFallback } from "../../utils/avatar-utils";
/* import { upload } from "../../utils/media-utils"; */
/* import { fetchReticulumAuthenticated } from "../../utils/phoenix-utils"; */
import {
  makeStyles,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  OverlayDrawer,
  DrawerHeader,
  Spinner,
  tokens
} from "@fluentui/react-components";
import { Input as FluentInput } from "@fluentui/react-components";
import { ArrowLeftFilled, Dismiss24Regular } from "@fluentui/react-icons";
import { AvatarManagementSidebar } from "./AvatarManagementSidebar";
import useBreakpoints from "../hooks/useBreakpoints";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import { getSignedUrl } from "../../utils/artwork-utils";
import { getAuthHeaders } from "../../utils/getToken";
import configs from "../../utils/configs";
import { METAVERSE_FOLDER } from "../../utils/media-utils";
import { isStorageLimitError } from "../../utils/storage-limit-utils";
import { uploadClaimFields } from "../../utils/upload-claim";

const AVATAR_EDITOR_SCENE_INTERACTION_SUPPRESSION_SOURCE = "avatar-editor";

function setAvatarEditorSceneInteractionSuppressed(suppressed) {
  if (window.APP?.setMainSceneInteractionSuppressed) {
    window.APP.setMainSceneInteractionSuppressed(suppressed, AVATAR_EDITOR_SCENE_INTERACTION_SUPPRESSION_SOURCE);
    return;
  }

  if (suppressed) {
    APP.scene.addState("in-modal-or-overlay");
  } else {
    APP.scene.removeState("in-modal-or-overlay");
  }
}

const useClasses = makeStyles({
  avatarEditorContainer: {
    width: "100dvw",
    height: "100dvh",
    minWidth: "100dvw",
    minHeight: "100dvh"
  },
  avatarEditor: {
    display: "flex",
    background: "var(--xr-viewer-background)",
    width: "100%",
    height: "100%"
  },
  preview: {
    width: "100%",
    height: "100%",
    borderRadius: "0"
  },
  avatarPreviewContainer: {
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden"
  },
  saveButton: {
    marginLeft: "auto"
  },
  textField: {
    width: "100%",
    marginBottom: "10px"
  },
  backButton: {
    border: "1px solid var(--colorNeutralForeground1)",
    width: "fit-content",
    "&:hover": {
      border: "1px solid var(--colorNeutralForeground1)"
    }
  },
  header: {
    position: "absolute",
    backgroundColor: "var(--colorNeutralBackground1)",
    padding: "10px 20px",
    display: "flex",
    flexDirection: "row",
    "@media (min-width: 992px)": {
      backgroundColor: "transparent",
      padding: "40px 50px 10px"
    }
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    pointerEvents: "none",
    transition: "opacity 0.2s ease-in-out"
  },
  spinnerContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backdropFilter: "blur(4px)"
  }
});

/* const AVATARS_API = "/api/v1/avatars"; */

const STEPS = {
  SELECT_AVATAR: "select-avatar",
  SELECT_TEMPLATE: "select-template",
  CUSTOMIZE_AVATAR: "customize-avatar",
  CUSTOMIZE_UPLOADED_AVATAR: "customize-uploaded-avatar"
};

const ZOOM_STATE_FULL_BODY = "fullBody";
const ZOOM_STATE_HEAD = "head";
const ZOOM_STATE_FEET = "feet";

export function AvatarEditor({ avatarId, onClose, onAvatarSelected, onSpawnAvatar, isSignedIn, store, ...props }) {
  const { workspaces, getDefaultWorkspace } = useWorkspaceContext();
  const classes = useClasses();
  const avatarPreviewRef = useRef(null);
  const intl = useIntl();

  const [currentAvatarGltfUrl, setCurrentAvatarGltfUrl] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [avatarName, setAvatarName] = useState(
    intl.formatMessage({ id: "avatar-editor.default-avatar-name", defaultMessage: "My Avatar" })
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showStorageLimitDialog, setShowStorageLimitDialog] = useState(false);
  const [step, setStep] = useState(STEPS.SELECT_AVATAR);
  const [initialAvatarUrl, setInitialAvatarUrl] = useState("");
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [currentZoomLevel, setCurrentZoomLevel] = useState(ZOOM_STATE_FULL_BODY);
  const [isEquipping, setIsEquipping] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(null);

  let handleZoomRef = null;
  const goBack = () => {
    setStep(STEPS.SELECT_AVATAR);
    setCurrentAvatar(null);
    setCurrentAvatarGltfUrl(initialAvatarUrl);
    if (handleZoomRef) handleZoomRef(false);
  };

  const lastLoadedAvatarIdRef = useRef(null);
  useEffect(() => {
    if (lastLoadedAvatarIdRef.current === avatarId) return;

    const getAvatarUrl = async () => {
      const { avatarSrc } = await resolveAvatarSrcWithFallback(avatarId, { store });

      setCurrentAvatarGltfUrl(avatarSrc);
      setInitialAvatarUrl(avatarSrc);
      lastLoadedAvatarIdRef.current = avatarId;
    };

    getAvatarUrl();
  }, [avatarId, store]);

  useEffect(() => {
    setAvatarEditorSceneInteractionSuppressed(true);

    if (window.APP && window.APP.setMainSceneRenderSuppressed) {
      window.APP.setMainSceneRenderSuppressed(true, "avatar-editor");
    }

    return () => {
      setAvatarEditorSceneInteractionSuppressed(false);

      if (window.APP && window.APP.setMainSceneRenderSuppressed) {
        window.APP.setMainSceneRenderSuppressed(false, "avatar-editor");
      }
    };
  }, []);

  const breakpoint = useBreakpoints();
  const isMobile = ["xs", "sm", "md"].includes(breakpoint);

  /*
  const createOrUpdateAvatarAPI = async avatarData => {
    const response = await fetchReticulumAuthenticated(AVATARS_API, "POST", {
      avatar: avatarData
    });
    if (response && response.avatars && response.avatars.length > 0) {
      return response.avatars[0];
    }
    const errorMsg = response?.error || "Failed to create avatar: Invalid API response.";
    throw new Error(errorMsg);
  };
  */

  const handleZoom = (zoom = ZOOM_STATE_FULL_BODY) => {
    if (!avatarPreviewRef.current) {
      setCurrentZoomLevel(zoom);
      return;
    }

    const preview = avatarPreviewRef.current;

    switch (zoom) {
      case ZOOM_STATE_HEAD:
        if (typeof preview.zoomToHead === "function") {
          if (ZOOM_STATE_FEET === currentZoomLevel) {
            preview.resetCamera();
          }
          preview.zoomToHead();
        }
        break;
      case ZOOM_STATE_FEET:
        if (typeof preview.zoomToFeet === "function") {
          if (!isMobile) return;

          if (ZOOM_STATE_HEAD === currentZoomLevel) {
            preview.resetCamera();
          }
          preview.zoomToFeet();
        }
        break;
      case ZOOM_STATE_FULL_BODY:
      default:
        if (typeof preview.resetZoom === "function") {
          preview.resetZoom();
        }
        break;
    }

    setCurrentZoomLevel(zoom);
  };

  const resetFeetZoom = () => {
    if (currentZoomLevel === ZOOM_STATE_FEET) {
      handleZoom(ZOOM_STATE_FULL_BODY);
    }
  };

  handleZoomRef = handleZoom;

  const handleCloseSaveDialog = () => {
    setShowSaveDialog(false);
    setTimeout(() => {
      setAvatarName(intl.formatMessage({ id: "avatar-editor.default-avatar-name", defaultMessage: "My Avatar" }));
    }, 100);
  };

  const handleInitiateSave = () => {
    setSaveError(null);
    setShowSaveDialog(true);
  };

  const onSaveAvatar = async () => {
    if (!avatarName.trim()) {
      setSaveError(
        intl.formatMessage({ id: "avatar-editor.error.name-required", defaultMessage: "Avatar name is required." })
      );
      return;
    }
    setIsSaving(true);
    setSaveError(null);

    const canCreateAssets = workspace =>
      workspace?.authorization?.projection_status === "ready" &&
      Array.isArray(workspace.authorization.permissions) &&
      workspace.authorization.permissions.includes("asset:create");
    const workspaceCandidates = [getDefaultWorkspace(), ...workspaces.filter(workspace => workspace.is_default), ...workspaces];
    const effectiveWorkspace = workspaceCandidates.find(canCreateAssets);

    if (!effectiveWorkspace) {
      setSaveError(
        "No workspace found to save the avatar. Please ensure you have contributor access to at least one workspace."
      );
      setIsSaving(false);
      return;
    }

    try {
      if (!avatarPreviewRef.current || typeof avatarPreviewRef.current.exportAvatar !== "function") {
        throw new Error("Avatar preview component is not ready or exportAvatar method is missing.");
      }

      // 1. Export GLB
      const glbBlob = await avatarPreviewRef.current.exportAvatar();
      if (!glbBlob) {
        throw new Error("Failed to export avatar GLB from preview.");
      }

      // 2. Export Thumbnail
      if (typeof avatarPreviewRef.current.snapshot !== "function") {
        throw new Error("Avatar preview component's snapshot method is missing.");
      }
      avatarPreviewRef.current.resetCamera();
      const thumbnailBlob = await avatarPreviewRef.current.snapshot();
      if (!thumbnailBlob) {
        throw new Error("Failed to create thumbnail snapshot.");
      }

      const glbFile = new File([glbBlob], `${avatarName}.glb`, { type: "model/gltf-binary" });
      const thumbnailFile = new File([thumbnailBlob], `${avatarName}-thumb.png`, { type: "image/png" });

        // 3. Get Signed URLs and Upload
      const [glbSigned, thumbSigned] = await Promise.all([
        getSignedUrl({ file: glbFile, folder: METAVERSE_FOLDER, workspaceId: effectiveWorkspace.id }),
        getSignedUrl({ file: thumbnailFile, folder: METAVERSE_FOLDER, workspaceId: effectiveWorkspace.id })
      ]);

      await Promise.all([
        fetch(glbSigned.presigned_url, {
          method: "PUT",
          headers: {
            "Content-Type": "model/gltf-binary",
            "x-ms-blob-type": "BlockBlob"
          },
          body: glbFile
        }),
        fetch(thumbSigned.presigned_url, {
          method: "PUT",
          headers: {
            "Content-Type": "image/png",
            "x-ms-blob-type": "BlockBlob"
          },
          body: thumbnailFile
        })
      ]);

      // 4. Create Asset Entry in Persistence API
      const entry = {
        name: avatarName,
        path: glbSigned.key,
        type: "model",
        asset_type: "avatar",
        thumbnail: thumbSigned.download_url,
        public_thumbnail: thumbSigned.download_url,
        source: "metaverse",
        ...uploadClaimFields(glbSigned, thumbSigned)
      };

      const myMediaUrl = `${configs["PERSISTENCE_API_URL"]}/workspaces/${effectiveWorkspace.id}/assets`;
      const response = await fetch(myMediaUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(await getAuthHeaders(myMediaUrl, "POST"))
          },
        body: JSON.stringify(entry)
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || `Failed to create asset entry: ${response.status} ${response.statusText}`);
      }

      const savedAsset = await response.json();
      const savedAssetData = savedAsset.data || savedAsset;
      const assetId = savedAssetData.uuid || savedAssetData.id;
      if (!assetId) {
        throw new Error("Failed to create asset: Missing asset ID from response.");
      }

      // 5. Create Avatar Entry in Persistence API
      const avatarEntry = {
        name: avatarName,
        asset_id: assetId
      };

      const avatarApiUrl = `${configs["PERSISTENCE_API_URL"]}/workspaces/${effectiveWorkspace.id}/avatars`;
      const avatarResponse = await fetch(avatarApiUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(await getAuthHeaders(avatarApiUrl, "POST"))
          },
        body: JSON.stringify(avatarEntry)
      });

      if (!avatarResponse.ok) {
        const errJson = await avatarResponse.json().catch(() => ({}));
        throw new Error(
          errJson.error || `Failed to create avatar entry: ${avatarResponse.status} ${avatarResponse.statusText}`
        );
      }

      const savedAvatar = await avatarResponse.json();
      const savedAvatarData = savedAvatar.data || savedAvatar;

      const finalAvatar = {
        ...savedAvatarData,
        avatar_id: savedAvatarData.sid,
        id: savedAvatarData.sid,
        asset_url: savedAssetData.url,
        url: savedAssetData.url, // Ensure URL is present for immediate use
        thumbnail: thumbSigned.download_url
      };

      console.log("[AvatarEditor] Auto-selecting new avatar:", finalAvatar);

      if (props.onSave) {
        props.onSave(finalAvatar);
      }

      // Auto-select the newly created avatar
      if (onAvatarSelected) {
        onAvatarSelected(finalAvatar);
      }

      setStep(STEPS.SELECT_AVATAR);
      handleZoom(ZOOM_STATE_FULL_BODY);
      handleCloseSaveDialog(); // Reset dialog and avatarName after save
    } catch (error) {
      console.error("Error saving avatar:", error);
      if (isStorageLimitError(error)) {
        setShowSaveDialog(false);
        setShowStorageLimitDialog(true);
      } else {
        setShowSaveDialog(true);
        setSaveError(
          intl.formatMessage({
            id: "avatar-editor.error.generic",
            defaultMessage: "There was an error creating your avatar. Please try again."
          })
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarChange = avatar => {
    onAvatarSelected(avatar);
  };

  const handleAvatarUpload = async avatar => {
    setCurrentAvatarGltfUrl(avatar.gltfUrl);
    handleZoom(ZOOM_STATE_FULL_BODY);
    setStep(STEPS.CUSTOMIZE_UPLOADED_AVATAR);
  };

  const handleSpawnAvatar = avatar => {
    if (isSaving) return;

    onSpawnAvatar(avatar);
    onClose();
  };

  const storeRef = useRef(store);

  useEffect(() => {
    const storeInstance = storeRef.current;
    const previouslyEnabled = storeInstance.state.preferences.disableIdleDetection;
    storeInstance.update({ preferences: { disableIdleDetection: true } });
    return () => {
      if (!previouslyEnabled) {
        storeInstance.update({ preferences: { disableIdleDetection: false } });
      }
    };
  }, []);

  return (
    <OverlayDrawer open={true} className={classes.avatarEditorContainer} modalType="non-modal">
      <section className={classes.avatarEditor}>
        <div className={classes.avatarPreviewContainer}>
          <DrawerHeader className={classes.header}>
            <Button
              appearance="outline"
              className={classes.backButton}
              onClick={() => {
                if (step === STEPS.CUSTOMIZE_AVATAR || step === STEPS.CUSTOMIZE_UPLOADED_AVATAR) {
                  setShowUnsavedWarning(true);
                } else if (step === STEPS.SELECT_AVATAR) {
                  if (!isSaving) onClose();
                } else {
                  goBack();
                }
              }}
              icon={<ArrowLeftFilled />}
            >
              {step === STEPS.SELECT_AVATAR && (
                <FormattedMessage id="avatar-editor.close-button" defaultMessage="Close" />
              )}
              {step !== STEPS.SELECT_AVATAR && (
                <FormattedMessage id="avatar-editor.back-button" defaultMessage="Back" />
              )}
            </Button>

            {(step === STEPS.CUSTOMIZE_AVATAR || step === STEPS.CUSTOMIZE_UPLOADED_AVATAR) && (
              <Button
                appearance="primary"
                onClick={handleInitiateSave}
                className={classes.saveButton}
                disabled={isSaving}
              >
                {isSaving ? (
                  <FormattedMessage id="avatar-editor.saving-button" defaultMessage="Saving..." />
                ) : (
                  <FormattedMessage id="avatar-editor.save-avatar-button" defaultMessage="Save" />
                )}
              </Button>
            )}
          </DrawerHeader>
          <AvatarPreview
            ref={avatarPreviewRef}
            avatarGltfUrl={currentAvatarGltfUrl}
            className={classes.preview}
            avatarCreator={true}
            preserveCamera={currentAvatar?.preserveCamera}
            showAnimationProfileSelector={step === STEPS.CUSTOMIZE_UPLOADED_AVATAR}
          ></AvatarPreview>
          {isEquipping && (
            <div className={classes.loadingOverlay}>
              <div className={classes.spinnerContainer}>
                <Spinner />
              </div>
            </div>
          )}
        </div>

        <UnsavedChangesWarning
          open={showUnsavedWarning}
          onOpenChange={(_, data) => setShowUnsavedWarning(data.open)}
          onSave={() => {
            setShowUnsavedWarning(false);
            handleInitiateSave();
          }}
          onCancel={() => {
            setShowUnsavedWarning(false);
            goBack();
          }}
        />

        {step === STEPS.SELECT_AVATAR && (
          <AvatarManagementSidebar
            onAvatarSelected={handleAvatarChange}
            onSpawnAvatar={onSpawnAvatar ? handleSpawnAvatar : undefined}
            onAvatarUpload={handleAvatarUpload}
            onClose={onClose}
            isSignedIn={isSignedIn}
            initialAvatarId={avatarId || props.initialAvatarId}
            performConditionalSignIn={props.performConditionalSignIn}
            onCreateAvatar={() => {
              setStep(STEPS.SELECT_TEMPLATE);
            }}
          />
        )}

        {(step === STEPS.SELECT_TEMPLATE || step === STEPS.CUSTOMIZE_AVATAR) && (
          <AvatarHanger
            onAvatarChange={avatar => {
              if (!isSaving) {
                setCurrentAvatarGltfUrl(avatar.previewUrl);
                setCurrentAvatar(avatar);
              }
            }}
            zoomToHead={() => handleZoom(ZOOM_STATE_HEAD)}
            zoomToFeet={() => handleZoom(ZOOM_STATE_FEET)}
            resetZoom={() => handleZoom(ZOOM_STATE_FULL_BODY)}
            resetFeetZoom={() => resetFeetZoom()}
            onEquipLoadingChange={setIsEquipping}
            disabled={isSaving}
            step={step}
            setStep={setStep}
            STEPS={STEPS}
          />
        )}

        <Dialog
          open={showSaveDialog}
          onOpenChange={(_, data) => {
            if (!isSaving) {
              setShowSaveDialog(data.open);
              if (!data.open) setAvatarName("My Avatar");
            }
          }}
          modalType="modal"
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle
                action={
                  <Button
                    appearance="subtle"
                    aria-label={intl.formatMessage({ id: "dialog.close-button", defaultMessage: "Close" })}
                    icon={<Dismiss24Regular />}
                    onClick={() => !isSaving && handleCloseSaveDialog()}
                    disabled={isSaving}
                  />
                }
              >
                <FormattedMessage id="avatar-editor.save-dialog.title" defaultMessage="Save Your Avatar" />
              </DialogTitle>
              <DialogContent>
                <FluentInput
                  appearance="filled-darker"
                  required
                  value={avatarName}
                  onChange={e => setAvatarName(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: "avatar-editor.field.avatar-name.placeholder",
                    defaultMessage: "Enter avatar name"
                  })}
                  className={classes.textField}
                  disabled={isSaving}
                />
                <p>
                  <FormattedMessage
                    id="avatar-editor.save-dialog.message"
                    defaultMessage="We're almost ready! To complete the creation you must enter a name and save your avatar."
                  />
                </p>
                {saveError && (
                  <p style={{ color: "red", marginTop: "10px" }}>
                    <FormattedMessage id="avatar-editor.error.save.prefix" defaultMessage="Error:" />
                    {saveError}
                  </p>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => !isSaving && handleCloseSaveDialog()} disabled={isSaving}>
                  <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                </Button>
                <Button appearance="primary" onClick={onSaveAvatar} disabled={isSaving || !avatarName.trim()}>
                  {isSaving ? (
                    <FormattedMessage id="avatar-editor.saving-button" defaultMessage="Saving..." />
                  ) : (
                    <FormattedMessage id="avatar-editor.submit-button.save" defaultMessage="Save" />
                  )}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        <Dialog
          open={showStorageLimitDialog}
          onOpenChange={(_, data) => setShowStorageLimitDialog(data.open)}
          modalType="modal"
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle
                action={
                  <Button
                    appearance="subtle"
                    aria-label={intl.formatMessage({ id: "dialog.close-button", defaultMessage: "Close" })}
                    icon={<Dismiss24Regular />}
                    onClick={() => setShowStorageLimitDialog(false)}
                  />
                }
              >
                <FormattedMessage id="storage-limit-modal.title" defaultMessage="Storage Limit Reached" />
              </DialogTitle>
              <DialogContent>
                <FormattedMessage
                  id="storage-limit-modal.message"
                  defaultMessage="You've reached your storage limit. Upgrade now to enjoy more space and uninterrupted access to your files."
                />
              </DialogContent>
              <DialogActions>
                <Button appearance="outline" onClick={() => setShowStorageLimitDialog(false)}>
                  <FormattedMessage id="storage-limit-modal.close-button" defaultMessage="I understand" />
                </Button>
                <Button appearance="primary" as="a" href={configs.UPGRADE_URL} target="_blank">
                  <FormattedMessage id="storage-limit-modal.upgrade-button" defaultMessage="Upgrade now" />
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </section>
    </OverlayDrawer>
  );
}

AvatarEditor.propTypes = {
  avatarId: propTypes.string,
  onClose: propTypes.func.isRequired,
  onAvatarSelected: propTypes.func,
  onSave: propTypes.func,
  children: propTypes.node,
  props: propTypes.object,
  hubChannel: propTypes.object,
  initialAvatarId: propTypes.string,
  performConditionalSignIn: propTypes.func,
  onSpawnAvatar: propTypes.func,
  isSignedIn: propTypes.bool,
  store: propTypes.object.isRequired
};

function UnsavedChangesWarning({ open, onOpenChange, onSave, onCancel }) {
  const intl = useIntl();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label={intl.formatMessage({ id: "dialog.close-button", defaultMessage: "Close" })}
                icon={<Dismiss24Regular />}
                onClick={() => onOpenChange(null, { open: false })}
              />
            }
          >
            <FormattedMessage id="avatar-editor.unsaved-changes-warning" defaultMessage="Heads-Up!" />
          </DialogTitle>
          <DialogContent>
            <FormattedMessage
              id="avatar-editor.unsaved-changes-warning.message"
              defaultMessage="You have pending changes to save. Do you want to save them before going back?"
            />
          </DialogContent>
          <DialogActions>
            <Button appearance="outline" onClick={onCancel}>
              <FormattedMessage id="button.no" defaultMessage="No" />
            </Button>
            <Button appearance="primary" onClick={onSave}>
              <FormattedMessage id="button.yes" defaultMessage="Yes" />
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

UnsavedChangesWarning.propTypes = {
  open: propTypes.bool.isRequired,
  onOpenChange: propTypes.func.isRequired,
  onSave: propTypes.func,
  onCancel: propTypes.func
};
